import { useEffect } from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  ListingApplicationMailStatusDocument,
  MailDeliveryStatus,
  type ListingApplicationRowFragment,
} from "@/graphql/generated/operations";

/** genesis sendet im Hintergrund; so oft und so lange fragt die Liste nach: zwölfmal alle fünf Sekunden. */
export const PENDING_MAIL_POLL_MS = 5000;
export const PENDING_MAIL_POLLS = 12;

const PENDING = new Set<MailDeliveryStatus>([MailDeliveryStatus.Queued, MailDeliveryStatus.Sending]);

// Lädt den Versandstand der Bewerbungen nach, deren Mail noch wartet oder gesendet wird
// (@external-adr GENESIS-0081-genesis-sends-mail-through-a-durable-outbox-and-never-repeats-an-uncertain-send).
// Einmal für die ganze Liste und nur für diese Bewerbungen. Das Intervall hängt an der Menge der wartenden Mails, nicht
// an der Identität der Daten: eine unveränderte Antwort beendet es nicht. Es endet, sobald keine Mail mehr wartet,
// nach PENDING_MAIL_POLLS Abrufen oder beim Verlassen der Liste; kommt eine neue wartende Mail hinzu, auch in einer
// Bewerbung, die schon eine wartende hat, beginnt es neu. Ein fehlgeschlagener Abruf zählt mit und wird beim nächsten
// Takt wiederholt.
export function usePendingMailRefresh(applications: readonly ListingApplicationRowFragment[]): void {
  const client = useApolloClient();
  const pending = applications
    .flatMap((application) => (application.mails ?? [])
      .filter((mail) => PENDING.has(mail.status))
      .map((mail) => `${application.id}:${mail.id}`))
    .sort()
    .join(",");
  useEffect(() => {
    if (!pending) return;
    const ids = [...new Set(pending.split(",").map((entry) => entry.split(":")[0]!))];
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls >= PENDING_MAIL_POLLS) clearInterval(timer);
      client.query({ query: ListingApplicationMailStatusDocument, variables: { ids }, fetchPolicy: "network-only" })
        .catch(() => {
          // Kein Status ist kein Fehler der Seite: der nächste Takt fragt wieder, die Zeile zeigt den letzten Stand.
        });
    }, PENDING_MAIL_POLL_MS);
    return () => clearInterval(timer);
  }, [client, pending]);
}
